package com.ijoin.data.push

import com.ijoin.data.model.PushPlatform
import com.ijoin.data.repository.PushTokenRepository
import com.ijoin.data.storage.TokenManager
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.launch
import kotlinx.coroutines.sync.Mutex
import kotlinx.coroutines.sync.withLock

/**
 * Lightweight helper that platform-specific code can call once a device push token
 * (FCM/APNs/WebPush) becomes available. Keeps the latest token and submits it to the backend.
 */
class PushTokenManager(
    private val repository: PushTokenRepository = PushTokenRepository(),
    private val tokenManager: TokenManager = TokenManager(),
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val mutex = Mutex()
    private var pendingPayload: TokenPayload? = null
    private var syncedPayload: TokenPayload? = null

    fun submitToken(token: String, platform: PushPlatform, deviceName: String? = null) {
        scope.launch {
            mutex.withLock {
                val payload = TokenPayload(token, platform, deviceName)
                pendingPayload = payload
                if (!tokenManager.isAuthenticated()) {
                    // User is not authenticated yet; retry when auth becomes available.
                    return@withLock
                }
                registerPendingLocked()
            }
        }
    }

    fun invalidateToken(token: String? = null) {
        scope.launch {
            mutex.withLock {
                val resolvedToken = token ?: syncedPayload?.token ?: pendingPayload?.token ?: return@withLock
                if (!tokenManager.isAuthenticated()) {
                    clearCache(resolvedToken)
                    return@withLock
                }
                repository.deleteToken(resolvedToken)
                    .onFailure { println("Failed to unregister push token: ${it.message}") }
                clearCache(resolvedToken)
            }
        }
    }

    fun onAuthenticationAvailable() {
        scope.launch {
            mutex.withLock {
                if (!tokenManager.isAuthenticated()) {
                    return@withLock
                }
                registerPendingLocked()
            }
        }
    }

    private suspend fun registerPendingLocked() {
        val payload = pendingPayload ?: return
        if (payload == syncedPayload) {
            return
        }
        val result = repository.registerToken(payload.token, payload.platform, payload.deviceName)
        if (result.isSuccess) {
            syncedPayload = payload
        } else {
            result.exceptionOrNull()?.message?.let { println("Push token registration failed: $it") }
        }
    }

    private fun clearCache(token: String) {
        if (pendingPayload?.token == token) {
            pendingPayload = null
        }
        if (syncedPayload?.token == token) {
            syncedPayload = null
        }
    }

    private data class TokenPayload(
        val token: String,
        val platform: PushPlatform,
        val deviceName: String? = null,
    )
}
